import { Fragment } from 'react';

import type { ScriptElement, TimelineContent } from '../../schema';

const STEM_ZONE = 22; // px reserved between an entry and the line for its stem
const MARKER = 26; // marker circle diameter

/**
 * Horizontal event timeline: a continuous accent line with numbered circle
 * markers, each connected by a short stem to its entry — entries alternate
 * above/below the line (1st above, 2nd below, …). Label is bold, body regular;
 * `style.fontSize` drives the body size and `style.color` the text color, so
 * it works on light and dark slide backgrounds alike.
 */
export function TimelineElement({
  element,
  fill,
}: {
  element: ScriptElement;
  fill?: boolean;
}) {
  const content = element.content as TimelineContent | undefined;
  const items = content?.items ?? [];
  if (items.length === 0) return null;
  const accent = content?.accentColor ?? '#f59e0b';
  const size = (element.style?.fontSize as number | undefined) ?? 16;
  const color = (element.style?.color as string | undefined) ?? '#1f2937';
  // Marker interior — authors on dark boards set the element background color
  // to the board color so the circle reads as an outline.
  const markerBg = (element.style?.backgroundColor as string | undefined) ?? '#ffffff';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${items.length}, 1fr)`,
        gridTemplateRows: '1fr auto 1fr',
        columnGap: 16,
        width: '100%',
        position: 'relative',
        ...(fill ? { height: '100%' } : null),
      }}
    >
      {/* The continuous line behind every marker. */}
      <div
        style={{
          gridRow: 2,
          gridColumn: '1 / -1',
          alignSelf: 'center',
          height: 2,
          width: '100%',
          background: accent,
        }}
      />
      {items.map((it, i) => {
        const above = i % 2 === 0;
        const entryRow = above ? 1 : 3;
        return (
          <Fragment key={it.id ?? i}>
            <div
              style={{
                gridRow: entryRow,
                gridColumn: i + 1,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: above ? 'flex-end' : 'flex-start',
                alignItems: 'center',
                textAlign: 'center',
                paddingBottom: above ? STEM_ZONE : 0,
                paddingTop: above ? 0 : STEM_ZONE,
                minWidth: 0,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: Math.round(size * 1.1), color }}>
                {it.label}
              </div>
              {it.body?.trim() && (
                <div
                  style={{
                    fontWeight: 400,
                    fontSize: size,
                    color,
                    lineHeight: 1.45,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {it.body}
                </div>
              )}
              {/* Stem from the entry toward the marker. */}
              <div
                style={{
                  position: 'absolute',
                  left: 'calc(50% - 1px)',
                  width: 2,
                  height: STEM_ZONE - 6,
                  background: accent,
                  ...(above ? { bottom: 0 } : { top: 0 }),
                }}
              />
            </div>
            {/* Numbered marker sitting ON the line. */}
            <div
              style={{
                gridRow: 2,
                gridColumn: i + 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <div
                style={{
                  width: MARKER,
                  height: MARKER,
                  borderRadius: '50%',
                  border: `2px solid ${accent}`,
                  background: markerBg,
                  color: accent,
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 700,
                  fontSize: Math.max(10, Math.round(size * 0.7)),
                  lineHeight: 1,
                }}
              >
                {i + 1}
              </div>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
